import { createOpenAICompatible } from '@ai-sdk/openai-compatible';
import { defaultSettingsMiddleware, wrapLanguageModel } from 'ai';
import { produce } from 'immer';

import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';
import { removeProperties } from '#core/lib/utils/object.ts';

import { cogniteAiApiFetch } from '#plugin-chat/lib/ai-api/cognite-ai-api-fetch.ts';

/** Creates an OpenAI-compatible provider configured for Cognite's Atlas AI backend. */
function useCogniteAtlasAiBackend() {
  const cogniteClient = useCogniteClient();
  const headers = cogniteClient.getDefaultRequestHeaders();

  const cogniteAtlasAi = createOpenAICompatible({
    name: 'Cognite Atlas AI',
    apiKey: headers.Authorization,
    // biome-ignore lint/style/useNamingConvention: Property name defined by @ai-sdk/openai-compatible
    baseURL: `${cogniteClient.getBaseUrl()}/api/v1/projects/${cogniteClient.project}/ai`,
    headers: cogniteClient.getDefaultRequestHeaders(),
    // Custom fetch that handles Cognite API compatibility (header stripping, case conversion)
    // Cast needed: cogniteAiApiFetch is fetch-compatible but lacks optional `preconnect` method
    fetch: cogniteAiApiFetch as typeof fetch,
  });

  return cogniteAtlasAi;
}

/**
 * Provides a configured language model for Cognite Atlas AI with necessary workarounds.
 * Applies middleware to strip unsupported parameters and enforce deterministic behavior.
 */
export function useAtlasAiModel() {
  const cogniteAtlasAi = useCogniteAtlasAiBackend();

  return wrapLanguageModel({
    modelId: 'azure/gpt-5.1',
    model: cogniteAtlasAi('azure/gpt-5.1'),
    middleware: [
      {
        specificationVersion: 'v3',
        transformParams: async ({ params }) =>
          produce(params, (draft) => {
            // Cognite Atlas AI doesn't support toolChoice parameter
            delete draft.toolChoice;
            draft.tools?.forEach((tool) => {
              if ('inputSchema' in tool) {
                // Remove $schema to comply with Atlas AI's JSON Schema requirements
                tool.inputSchema = removeProperties(tool.inputSchema, ['$schema']);
              }
            });
          }),
      },
      defaultSettingsMiddleware({
        settings: {
          // Temperature 0 ensures deterministic responses for data extraction tasks
          temperature: 0,
        },
      }),
    ],
  });
}
